import { Component, OnInit } from '@angular/core';
import { ColumnData } from '../../shared/models/column-data.model';
import { Principal } from '../../../shared';
import { JhiLanguageService } from '../../../shared/core-utils';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpResponse } from '@angular/common/http';
import { Sample, SampleService } from '../../../entities/sample';
import { ActivatedRoute } from '@angular/router';
import { AlertService } from '../../../shared/alert/alert.service';
import { DELETE_SAMPLE_LISTS_PERMISSIONS, MS_IMPORT_GENOTYPES_FROM_FILE, MS_IMPORT_GENOTYPES_FROM_GIGWA, SAMPLE_LABEL_PRINTING_PERMISSIONS, VIEW_GERMPLASM_DETAILS_PERMISSION, VIEW_NAMES_PERMISSIONS } from '../../../shared/auth/permissions';
import { TranslateService } from '@ngx-translate/core';
import { COLUMN_SELECTOR_CHECKBOX_ID } from '../dataset/column-selector/column-selector.component';
import { ColumnDataService } from '../../shared/services/column-data.service';
import { StudyEditorContext } from '../../study-editor.context';
import { StudySampleList } from '../../../entities/sample/sample-list.model';
import { ModalConfirmComponent } from '../../../shared/modal/modal-confirm.component';
import { SampleGenotypeImportModalComponent } from '../../../entities/sample/genotype/sample-genotype-import-modal.component';
import { SampleGenotypeImportFileComponent } from '../../../entities/sample/genotype/sample-genotype-import-file.component';
import { StudyEditorService } from '../../study-editor.service';
import { GermplasmDetailsPopupService } from '../../../germplasm-details/germplasm-details-popup.service';
import { MAX_NAME_DISPLAY_SIZE } from '../../../app.constants';
import { preventDefaultOnShiftKey } from '../../../field-management/utils';
import { LabelPrintingType } from '../../../label-printing/label-printing.component';
import { getStaticColumnLabelKey } from '../../../shared/util/static-columns-util';

@Component({
    selector: 'jhi-sample-list',
    templateUrl: './sample-list.component.html',
    styleUrl: './sample-list.component.scss'
})
export class SampleListComponent implements OnInit {

    public readonly MAX_NAME_DISPLAY_SIZE = MAX_NAME_DISPLAY_SIZE;
    public readonly CHECKBOX: number = COLUMN_SELECTOR_CHECKBOX_ID;
    public readonly SAMPLE_ENTRY: number = -16;
    public readonly GID: number = 8240;
    public readonly DESIGNATION: number = 8250;
    public readonly PLOT_NO: number = 8200;
    public readonly SAMPLE_NO: number = -17;
    public readonly SAMPLE_NAME: number = -12;
    public readonly TAKEN_BY: number = -15;
    public readonly SAMPLING_DATE: number = -14;
    public readonly SAMPLE_UUID: number = -13;
    public readonly PLATE_ID: number = -18;
    public readonly WELL: number = -19;
    public readonly OBS_UNIT_ID: number = 8201;

    protected readonly Math = Math;
    protected readonly SAMPLE_LABEL_PRINTING_PERMISSIONS = SAMPLE_LABEL_PRINTING_PERMISSIONS;
    protected readonly DELETE_SAMPLE_LISTS_PERMISSIONS = DELETE_SAMPLE_LISTS_PERMISSIONS;
    protected readonly MS_IMPORT_GENOTYPES_FROM_GIGWA = MS_IMPORT_GENOTYPES_FROM_GIGWA;
    protected readonly MS_IMPORT_GENOTYPES_FROM_FILE = MS_IMPORT_GENOTYPES_FROM_FILE;

    protected readonly preventDefaultOnShiftKey = preventDefaultOnShiftKey;
    protected readonly getStaticColumnLabelKey = getStaticColumnLabelKey;

    sampleList: StudySampleList;
    displayedColumns: ColumnData[] = [];
    selectedItems: Set<number> = new Set<number>();

    entries: Sample[];
    listId: number;
    currentPage: number = 1;
    pageSize: number = 50;
    numberOfEntries: number;

    isReloadPending: boolean;
    reloadTimeout: any;
    isTableDataReloadPending: boolean;
    tableDataReloadTimeout: any;
    reloadDelay: number = 800;
    isSelectAll = false;
    lastClickIndex: any;
    hasViewNamesPermission = false;
    hasViewGermplasmDetailsPermission = false;

    columnDataService: ColumnDataService;
    isShowAction: boolean;


    constructor(private jhiLanguangeService: JhiLanguageService,
                private sampleService: SampleService,
                private activatedRoute: ActivatedRoute,
                private alertService: AlertService,
                private studyEditorContext: StudyEditorContext,
                private modalService: NgbModal,
                private translateService: TranslateService,
                public principal: Principal,
                private studyEditorService: StudyEditorService,
                public germplasmDetailsPopupService: GermplasmDetailsPopupService) {
    }

    ngOnInit(): void {

        this.isShowAction = this.principal.hasAnyAuthorityDirect(SAMPLE_LABEL_PRINTING_PERMISSIONS)
            || this.principal.hasAnyAuthorityDirect(DELETE_SAMPLE_LISTS_PERMISSIONS)
            || this.principal.hasAnyAuthorityDirect(MS_IMPORT_GENOTYPES_FROM_GIGWA)
            || this.principal.hasAnyAuthorityDirect(MS_IMPORT_GENOTYPES_FROM_FILE);

        this.hasViewGermplasmDetailsPermission = this.principal.hasAnyAuthorityDirect(VIEW_GERMPLASM_DETAILS_PERMISSION);
        this.hasViewNamesPermission = this.principal.hasAnyAuthorityDirect(VIEW_NAMES_PERMISSIONS);

        this.activatedRoute.params.subscribe(params => {
            this.listId = parseInt(params['listId'], 10);
            this.sampleList = this.studyEditorContext.sampleLists.find(sampleList => sampleList.listId === this.listId);
            this.columnDataService = new ColumnDataService(ColumnDataService.SAMPLE_LIST_TABLE_COLUMNS_KEY, String(this.listId));
            this.initialize();
        });

    }

    /**
     * Efficiently reload table
     */
    scheduleReload(): void {
        if (this.isReloadPending) {
            // If a reload is already scheduled, reset the delay (debouncing)
            clearTimeout(this.reloadTimeout);
        } else {
            this.isReloadPending = true; // Indicate that a reload has been scheduled
        }

        // Schedule the reload after the specified delay
        this.reloadTimeout = setTimeout(() => {
            this.loadTable();
            this.isReloadPending = false; // Reset pending flag after reload
        }, this.reloadDelay);
    }

    /**
     * Efficiently reload table data
     */
    scheduleTableDataReload(pageIndex, pageSize): void {
        if (this.isTableDataReloadPending) {
            // If a reload is already scheduled, reset the delay (debouncing)
            clearTimeout(this.tableDataReloadTimeout);
        } else {
            this.isTableDataReloadPending = true; // Indicate that a reload has been scheduled
        }

        // Schedule the reload after the specified delay
        this.tableDataReloadTimeout = setTimeout(() => {
            this.loadTableData(pageIndex, pageSize);
            this.isTableDataReloadPending = false; // Reset pending flag after reload
        }, this.reloadDelay);
    }

    initialize() {
        this.resetState();
        this.loadTable();
    }

    loadTable() {
        this.loadTableHeader().then(() => {
            this.loadTableData();
        });
    }

    async loadTableHeader() {

        const columns: ColumnData[] = [
            this.createColumnData(this.CHECKBOX, '', true, true),
            this.createColumnData(this.SAMPLE_ENTRY, 'SAMPLE_ENTRY', true, true),
            this.createColumnData(this.DESIGNATION, 'DESIGNATION', this.hasViewNamesPermission, true),
            this.createColumnData(this.GID, 'GID', true, true),
            this.createColumnData(this.PLOT_NO, 'PLOT_NO', true, true ),
            this.createColumnData(this.SAMPLE_NO, 'SAMPLE_NO', true, true),
            this.createColumnData(this.SAMPLE_NAME, 'SAMPLE_NAME', true, true),
            this.createColumnData(this.TAKEN_BY, 'TAKEN_BY', true, true),
            this.createColumnData(this.SAMPLING_DATE, 'SAMPLING_DATE', true, true),
            this.createColumnData(this.SAMPLE_UUID, 'SAMPLE_UUID', true, false),
            this.createColumnData(this.PLATE_ID, 'PLATE_ID', true, false),
            this.createColumnData(this.WELL, 'WELL', true, false),
            this.createColumnData(this.OBS_UNIT_ID, 'OBS_UNIT_ID', true, false),
        ];

        this.displayedColumns = this.columnDataService.displayedColumns = columns;
        this.displayedColumns = this.columnDataService.applyColumnsState();
    }

    loadTableData(pageIndex: any = 0, pageSize: any = 50): void {
        this.sampleService.query({
            page: pageIndex ?? this.currentPage - 1,
            size: pageSize ?? this.pageSize,
            listId: this.listId,
            sort: this.sort() }).toPromise().then((res: HttpResponse<Sample[]>) => {
            this.numberOfEntries = parseInt(res.headers.get('X-Total-Count'), 10);
            this.entries = res.body;
        });
    }

    onPageChange(page: number): void {
        this.currentPage = page;
        this.scheduleTableDataReload((page - 1), this.pageSize);
    }

    onPageSizeChange($event: Event) {
        this.loadTableData((this.currentPage - 1), this.pageSize);
    }

    removeEntries() {
        if (!this.validateSelection()) {
            return;
        }
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.message = this.translateService.instant('bmsjHipsterApp.sample.delete-entries.confirm.message');
        confirmModalRef.componentInstance.title = this.translateService.instant('bmsjHipsterApp.sample.delete-entries.confirm.header');

        confirmModalRef.result.then(() => {
            this.sampleService.removeEntries(this.sampleList.listId, Array.from(this.selectedItems)).toPromise().then(() => {
                this.selectedItems.clear();
                this.scheduleTableDataReload(1, this.pageSize);
                this.alertService.success('bmsjHipsterApp.sample.delete-entries.delete.success');
            });
        }, () => confirmModalRef.dismiss());
    }

    importFromGigwa() {
        const modal = this.modalService.open(SampleGenotypeImportModalComponent as Component, { size: 'lg', backdrop: 'static' });
        modal.componentInstance.listId = this.listId.toString();
        modal.componentInstance.studyId = this.studyEditorContext.studyId.toString();
        modal.result.then(() => {
            this.studyEditorService.triggerRefreshAndWait().then(() => {
                this.studyEditorService.navigateToTab(this.studyEditorContext.studyId, 'sample-genotype');
            });
        });
    }

    importFromFile() {
        const modal = this.modalService.open(SampleGenotypeImportFileComponent as Component, { size: 'lg', backdrop: 'static' });
        modal.componentInstance.listId = this.listId.toString();
        modal.componentInstance.studyId = this.studyEditorContext.studyId.toString();
        modal.result.then(() => {
            this.studyEditorService.triggerRefreshAndWait().then(() => {
                this.studyEditorService.navigateToTab(this.studyEditorContext.studyId, 'sample-genotype');
            });
        });
    }

    private validateSelection() {
        if (this.selectedItems.size === 0) {
            this.alertService.error('germplasm-list.list-data.selection.empty');
            return false;
        }
        return true;
    }

    private createColumnData(id: number, displayName: string, visible: boolean, selected: boolean): ColumnData {
        return {
            displayName,
            visible,
            columnSelectorVisible: visible,
            selected: selected,
            variableTypeId: null,
            id,
            sortingAsc: null,
            isFiltered: false,
            query: '',
            isSelectAll: false,
            possibleValuesById: {},
            possibleValuesByName: {},
            enableFilterSorting: true,
            deletable: false,
            hidden: false,
            inputVariables: '',
            termId: id,
            name: null,
            alias: null
        };
    }

    clearSelected() {
        this.selectedItems.clear();
    }

    selectAll(isSelectAll) {
        this.isSelectAll = isSelectAll;
        // Hide the checkbox column when select all is true
        if (isSelectAll) {
            this.displayedColumns.find(column => column.id === this.CHECKBOX).selected = false;
        } else {
            this.displayedColumns.find(column => column.id === this.CHECKBOX).selected = true;
        }
    }

    isSelected(sampleId: number): boolean {
        return this.selectedItems.has(sampleId);
    }

    toggleSelect($event, index: number, sample: Sample, checkbox = false) {

        if (this.isSelectAll) {
            return;
        }

        if ($event.ctrlKey || $event.metaKey) {
            $event.preventDefault();
        }

        if (!checkbox) {
            $event.preventDefault();
        }

        if (!$event.ctrlKey && !$event.metaKey && !checkbox) {
            this.selectedItems.clear();
        }

        let items: Sample[] = [];
        if ($event.shiftKey) {
            const max = Math.max(this.lastClickIndex, index) + 1;
            const min = Math.min(this.lastClickIndex, index);
            items = this.entries.slice(min, max);
        } else {
            items = [sample];
            this.lastClickIndex = index;
        }

        const isClickedItemSelected = this.selectedItems.has(sample.sampleId);
        for (const item of items) {
            if (isClickedItemSelected) {
                this.selectedItems.delete(item.sampleId);
            } else {
                this.selectedItems.add(item.sampleId);
            }
        }
    }

    onColumnSelectorDeselectedAll() {
        this.displayedColumns.forEach(column => {
            if (column.visible) {
                column.selected = false;
            }
        });
    }

    onColumnSelectorSelectedAll() {
        this.displayedColumns.forEach(column => {
            if (column.visible) {
                column.selected = true;
            }
        });
    }

    onColumnSelectorReset() {
        this.onColumnSelectorSelectedAll();
        this.scheduleReload();
    }

    resetState() {
        this.selectedItems.clear();
        this.currentPage = 1;
        this.isSelectAll = false;
        this.lastClickIndex = null;
    }

    private sort() {
        return '';
    }

    sampleListPrintingLabels() {
        const selectedColumns = this.displayedColumns.filter(column => column.selected).map(column => column.displayName);
        this.studyEditorService.navigateToSampleLabelPrintingView(this.studyEditorContext.studyId, null, this.listId, selectedColumns, LabelPrintingType.SAMPLE);
    }

    trackColumnsById(column: ColumnData) {
        return column.id;
    }

    protected onSelectPage() {
        if (this.isPageSelected()) {
            // remove all items
            this.entries.forEach((item) => this.selectedItems.delete(item.sampleId));
        } else {
            // check remaining items
            this.entries.forEach((item) => this.selectedItems.add(item.sampleId));
        }
    }

    protected isPageSelected() {
        return this.selectedItems && this.selectedItems.size && this.entries.every((item) => this.selectedItems.has(item.sampleId));
    }
}
